import { z } from "zod";
import { CryptoJsService } from "../crypto/crypto-js-service";
import { SentryService } from "../sentry/sentry-service";

export function isApiError(error: any): error is ApiService.ApiError {
  return error instanceof Error && "code" in error;
}

export namespace ApiService {
  type SuccessResponseCode =
    | 200
    | 201
    | 202
    | 203
    | 204
    | 205
    | 206
    | 207
    | 208
    | 226;

  export type ApiResponseSuccess<T> = {
    code: SuccessResponseCode;
    status: "success";
    data: T;
  };

  export type ApiResponseError = {
    code: number;
    status: "error";
    message: string;
  };

  export type ApiResponse<T> = ApiResponseSuccess<T> | ApiResponseError;

  export async function wrapResponse<T>(
    response: Promise<T>,
    successCode: SuccessResponseCode = 200,
    encrypt = process.env.NODE_ENV === "production",
  ): Promise<ApiResponse<string> | ApiResponse<T>> {
    try {
      const data = await response;
      if (!encrypt) {
        return {
          status: "success",
          code: successCode,
          data,
        };
      }
      const encryptedData = CryptoJsService.encrypt(
        JSON.stringify(data),
        process.env.NEXT_PUBLIC_KEY,
      );
      return { status: "success", code: successCode, data: encryptedData };
    } catch (error) {
      if (process.env.NODE_ENV === "production") {
        SentryService.captureException(error);
      } else {
        console.error(error);
      }

      if (isApiError(error)) {
        return {
          status: "error",
          message: error.message,
          code: error.code,
        };
      } else {
        return { status: "error", message: "An error occurred", code: 500 };
      }
    }
  }

  export class ApiError extends Error {
    code: number;

    constructor(message: string, code: number) {
      super(message);
      this.code = code;
    }
  }

  export function parseApiResponse<K extends z.ZodTypeAny>(
    data: unknown,
    schema: K,
    decrypt = process.env.NODE_ENV === "production",
  ) {
    const apiResponseSchema = z.union([
      z.object({
        code: z.number(),
        status: z.literal("error"),
        message: z.string(),
      }),
      z.object({
        code: z.number(),
        status: z.literal("success"),
        data: z.preprocess((data) => {
          if (!decrypt) {
            return data;
          }
          // return data;
          const decodedData = CryptoJsService.decrypt(
            String(data),
            process.env.NEXT_PUBLIC_KEY,
          );

          return decodedData;
        }, schema),
      }),
    ]);

    return apiResponseSchema.parse(data);
  }
}
